import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type {
  DashboardLayoutState,
  DashboardLayoutStore,
} from '../types/overview-types';

const initialLayoutState: DashboardLayoutState = {
  currentLayout: null,
  sectionLayouts: {},
  hasUnsavedLayoutChanges: false,
  lastFocusedSectionId: null,
  collapsedSections: new Set(),
};

export const useDashboardLayoutStore = create<DashboardLayoutStore>()(
  devtools(
    (set, get) => ({
      ...initialLayoutState,

      setCurrentLayout: layout => set({ currentLayout: layout }, false, 'setCurrentLayout'),

      setSectionLayout: (sectionId, layout) =>
        set(
          state => ({
            sectionLayouts: {
              ...state.sectionLayouts,
              [sectionId]: layout,
            },
          }),
          false,
          'setSectionLayout'
        ),

      getSectionLayout: sectionId => {
        const state = get();
        return state.sectionLayouts[sectionId] ?? null;
      },

      setLastFocusedSectionId: sectionId =>
        set({ lastFocusedSectionId: sectionId }, false, 'setLastFocusedSectionId'),

      setHasUnsavedLayoutChanges: hasChanges =>
        set({ hasUnsavedLayoutChanges: hasChanges }, false, 'setHasUnsavedLayoutChanges'),

      markLayoutAsSaved: () =>
        set({ hasUnsavedLayoutChanges: false }, false, 'markLayoutAsSaved'),

      resetLayoutState: () => set(initialLayoutState, false, 'resetLayoutState'),

      clearSectionLayouts: () =>
        set({ sectionLayouts: {}, lastFocusedSectionId: null }, false, 'clearSectionLayouts'),

      toggleSectionCollapse: sectionId =>
        set(
          state => {
            const next = new Set(state.collapsedSections);
            if (next.has(sectionId)) {
              next.delete(sectionId);
            } else {
              next.add(sectionId);
            }
            return { collapsedSections: next };
          },
          false,
          'toggleSectionCollapse'
        ),

      isSectionCollapsed: sectionId => {
        const state = get();
        return state.collapsedSections.has(sectionId);
      },

      initCollapseState: sections => {
        const collapsedIds: string[] = [];
        for (const section of sections) {
          if (section.collapsed) {
            collapsedIds.push(section.id);
          }
        }
        return set(
          {
            collapsedSections: new Set(collapsedIds),
          },
          false,
          'initCollapseState'
        );
      },
    }),
    { name: 'DashboardLayoutStore', enabled: process.env.NODE_ENV === 'development' }
  )
);

export default useDashboardLayoutStore;
